import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { quickbooksContract } from "@common/contracts/quickbooks.contract";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "@common/types/integrationConnection";
import { IntegrationConnectionDto } from "@common/dto/integrationConnection/integration-connection.dto";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { integrationConnectionQueryKeys } from "./integrationConnection.api";

const quickbooksContractClient = getBackendAPIClient(quickbooksContract);

export function useQuickbooksConnectionCreationMutation(
    onSuccess?: (connection: IntegrationConnection) => void,
    workspaceIdOverride?: string,
) {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();
    const targetWorkspaceId = workspaceIdOverride ?? workspaceId ?? undefined;

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof quickbooksContract.createConnection
                >["body"],
                "workspaceId"
            >,
        ) => {
            const response = await quickbooksContractClient.createConnection({
                body: {
                    ...data,
                    workspaceId: targetWorkspaceId,
                },
            });

            return IntegrationConnectionDto.deserialize(response.body);
        },
        onSuccess: async (connection) => {
            const queryKey = integrationConnectionQueryKeys.query(
                targetWorkspaceId ?? "",
                { provider: ConnectionProvider.QUICKBOOKS },
            );
            await queryClient.cancelQueries({ queryKey, exact: true });
            queryClient.setQueryData<IntegrationConnection[]>(
                queryKey,
                (connections) => {
                    if (!connections) {
                        return connections;
                    }
                    const hasExistingConnection = connections.some(
                        (candidate) => candidate.id === connection.id,
                    );
                    if (!hasExistingConnection) {
                        return [...connections, connection];
                    }
                    return connections.map((candidate) =>
                        candidate.id === connection.id
                            ? {
                                  ...connection,
                                  createdByUser:
                                      connection.createdByUser ??
                                      candidate.createdByUser,
                                  integrationAccounts:
                                      connection.integrationAccounts ??
                                      candidate.integrationAccounts,
                              }
                            : candidate,
                    );
                },
            );
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.queries(
                    targetWorkspaceId ?? "",
                ),
            });
            onSuccess?.(connection);
        },
    });
}
